import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { diaryInputSchema, type DiaryDocType } from '@ast-data/shared';
import { dateColumn, type GridColDef } from '../../lib/dataGrid';
import { useRecordActionsColumn } from '../../components/useGridRowActions';
import { useAuditColumns } from '../../components/auditColumns';
import { HIDDEN_AUDIT_COLUMNS } from '../../components/auditFields';
import {
    useInlineEditColumn,
    type InlineField
} from '../../components/gridInlineEdit';
import { joinedToChips } from '../../components/formFields';
import { useIsoWeekColumn } from '../../components/isoWeekColumn';
import { usePersonPicker } from '../../components/personPicker';
import {
    resolveKeepingStored,
    storedPersonLinkList,
    usePersonUserId
} from '../users/personUserId';
import { formatDate } from '../../utils/formatDate';
import { formatTemperature } from '../../utils/weatherTemperature';

/**
 * The Tagebuch fields editable in the grid — one definition for the columns and
 * the save, like the Fotos (see useInlinePhotoFields).
 *
 * A Tagebuch is almost entirely the record of ONE day: the weather, the working
 * times, the day's course, the running numbers it ends on. None of that may be
 * written across a set of days, and since the Sammelbearbeitung offers exactly
 * what the cells edit, none of it is a cell either.
 *
 * What is left is what holds over a Maßnahme rather than over a day — who wrote
 * the book, who led the dig, which Techniker was on it. A name mistyped through
 * a whole week is the reason this exists at all.
 *
 * The Personal stays out on purpose: it is the day's crew, and the record
 * dialog already fills it from what was actually recorded that day (see
 * DiaryDayPeopleList). Overwriting twenty days of it from one dialog would work
 * against that.
 *
 * Every name carries its account link along, resolved by the same rule the form
 * saves by — the Techniker positionally, since it is a list.
 *
 * Takes no rows: the three are person pickers, and a person picker offers the
 * app's accounts plus whatever the ROW being edited says — never the names
 * gathered off every other row (see components/personPicker).
 */
export function useInlineDiaryFields(): InlineField[] {
    const { accounts } = usePersonPicker();
    const { resolve } = usePersonUserId();

    return useMemo(
        () => [
            {
                field: 'author',
                editor: { kind: 'person' },
                derive: (value: unknown) => ({
                    authorId: resolve(String(value ?? ''))
                })
            },
            {
                field: 'excavationLeader',
                editor: { kind: 'person', leads: true },
                derive: (value: unknown) => ({
                    excavationLeaderId: resolve(String(value ?? ''))
                })
            },
            {
                /**
                 * Several people, and the record stores them ', '-joined — so
                 * unlike the Zeichner of a sheet this needs no `parse`, only
                 * the same split for the positional account links.
                 */
                field: 'technician',
                editor: { kind: 'person', multiple: true },
                /**
                 * The cell commits the WHOLE list, so the Techniker who
                 * stayed keep the accounts they had — only a name that is new
                 * to the day is resolved (see resolveKeepingStored).
                 */
                derive: (value: unknown, stored?: Record<string, unknown>) => ({
                    technicianIds: resolveKeepingStored(
                        joinedToChips(value, accounts),
                        storedPersonLinkList(
                            joinedToChips(stored?.technician, accounts),
                            stored?.technicianIds as string[] | undefined
                        ),
                        resolve
                    )
                })
            }
        ],
        [accounts, resolve]
    );
}

/** Stable empty default, so the column identity doesn't churn per render. */
const NO_DUPLICATES: ReadonlySet<string> = new Set();

// Compose the structured weather into one cell value.
function weatherLabel(row: DiaryDocType): string {
    return [
        row.weatherCondition,
        formatTemperature(row.weatherTemperature),
        row.weatherExtra
    ]
        .filter(Boolean)
        .join(', ');
}

/**
 * The rest of the day's record — the work log, the machine hours and the
 * weather in its parts — offered in the column picker and the filter panel but
 * hidden by default. The `*Id`/`*Ids` app-user and parcel links stay out, and
 * `visitors` already has its own column; the audit stamps come from
 * {@link useAuditColumns}, hidden the same way.
 */
const DIARY_DETAIL_FIELDS = [
    'technician',
    'staff',
    'workStart',
    'workEnd',
    'breakTime',
    'weatherCondition',
    'weatherTemperature',
    'weatherExtra',
    'topsoilRemoval',
    'surveying',
    'cleaning',
    'cuttingFindings',
    'drawing',
    'descriptions',
    'recovery',
    'finds',
    'photos',
    'excavatorType',
    'excavatorCount',
    'excavatorStart',
    'excavatorEnd',
    'excavatorBreak',
    'excavatorNote',
    'dailyCourse',
    'lastFindingNumber',
    'lastProfileNumber',
    'lastStrayFindNumber',
    'lastGeoprofileNumber',
    'lastSondageNumber',
    'lastPartialPhotoNumber'
] as const;

/** Detail columns shown anyway — the ones edited here (see the Funde grid). */
const VISIBLE_DIARY_DETAIL_FIELDS: readonly string[] = ['technician'];

/** `columnVisibilityModel` entries hiding the detail columns by default. */
export const HIDDEN_DIARY_DETAIL_COLUMNS: Record<string, boolean> = {
    ...Object.fromEntries(
        DIARY_DETAIL_FIELDS.filter(
            (field) => !VISIBLE_DIARY_DETAIL_FIELDS.includes(field)
        ).map((field) => [field, false])
    ),
    ...HIDDEN_AUDIT_COLUMNS
};

/** Column definitions for the Tagebuch (diary) grid. */
export function useDiaryColumns({
    canEdit,
    inlineFields = [],
    onEdit,
    onDelete,
    onHistory,
    duplicateIds = NO_DUPLICATES
}: {
    canEdit: boolean;
    /** Which columns are edited in the grid — see useInlineDiaryFields. */
    inlineFields?: readonly InlineField[];
    onEdit: (row: DiaryDocType) => void;
    onDelete: (row: DiaryDocType) => void;
    onHistory: (row: DiaryDocType) => void;
    /**
     * Entries whose day is recorded twice in their project (from
     * `computeDiaryDuplicates`) — marked in the date cell, the day's key.
     */
    duplicateIds?: ReadonlySet<string>;
}): GridColDef<DiaryDocType>[] {
    const intl = useIntl();
    const actionsColumn = useRecordActionsColumn<DiaryDocType>({
        canEdit,
        onEdit,
        onDelete,
        onHistory
    });
    const auditColumns = useAuditColumns<DiaryDocType>();
    const inlineEdit = useInlineEditColumn<DiaryDocType>(
        diaryInputSchema,
        canEdit
    );
    const inlineOf = (field: string) =>
        inlineFields.find((spec) => spec.field === field);
    const isoWeekColumn = useIsoWeekColumn<DiaryDocType>(
        (row) => row.recordedAt
    );

    return [
        dateColumn<DiaryDocType>({
            field: 'recordedAt',
            headerName: intl.formatMessage({ id: 'diary.field.recordedAt' }),
            width: 160,
            // Bold like the other entities' key column — it is the row's key.
            cellClassName: 'grid-cell-bold',
            // Plain formatted date, plus the warning when the day exists twice.
            renderCell: ({ value, row }) => (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        height: '100%'
                    }}
                >
                    {value ? formatDate(value as Date) : ''}
                    {duplicateIds.has(row.id) && (
                        <Tooltip
                            title={intl.formatMessage({
                                id: 'diary.duplicate.tooltip'
                            })}
                        >
                            <WarningAmberIcon
                                color="warning"
                                fontSize="small"
                            />
                        </Tooltip>
                    )}
                </Box>
            )
        }),
        isoWeekColumn,
        {
            field: 'author',
            headerName: intl.formatMessage({ id: 'diary.field.author' }),
            width: 140,
            ...(inlineOf('author')
                ? inlineEdit('author', inlineOf('author'))
                : {})
        },
        {
            field: 'excavationLeader',
            headerName: intl.formatMessage({
                id: 'diary.field.excavationLeader'
            }),
            width: 140,
            ...(inlineOf('excavationLeader')
                ? inlineEdit('excavationLeader', inlineOf('excavationLeader'))
                : {})
        },
        {
            field: 'weather',
            headerName: intl.formatMessage({ id: 'diary.field.weather' }),
            flex: 1,
            minWidth: 160,
            valueGetter: (_value, row) => weatherLabel(row)
        },
        {
            field: 'visitors',
            headerName: intl.formatMessage({ id: 'diary.field.visitors' }),
            width: 90,
            type: 'number',
            valueGetter: (_value, row) => row.visitors.length
        },
        ...DIARY_DETAIL_FIELDS.map<GridColDef<DiaryDocType>>((field) => ({
            field,
            headerName: intl.formatMessage({ id: `diary.field.${field}` }),
            width: 150,
            // The temperature is stored as the bare reading; the cell says °C.
            valueGetter:
                field === 'weatherTemperature'
                    ? (_value, row) => formatTemperature(row.weatherTemperature)
                    : undefined,
            ...(inlineOf(field) ? inlineEdit(field, inlineOf(field)) : {})
        })),
        ...auditColumns,
        actionsColumn
    ];
}
